import { Base } from 'src/api/Base'
import { merge } from 'lodash-es'

export class KpiAPI extends Base {
  static get entity() {
    return 'kpi'
  }

  static async parse({ metric }, { customCancelEvent } = {}) {
    const { dealerGroupId, customGroupId } = this.datepicker
    const params = {
      metric,
      customGroupId,
      dealerGroupId: !customGroupId ? dealerGroupId : 0,
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/parse`, {
      params,
    })
    return data
  }

  static async get(
    { metric, forceRecalculation = false },
    { shouldCancelOnRoute, customCancelEvent } = {}
  ) {
    const params = {
      metric,
      ...this.datepicker,
      forceRecalculation: Number(forceRecalculation),
    }
    const { data } = await this.request({
      shouldCancelOnRoute,
      customCancelEvent,
    }).get(`/${this.entity}/get`, {
      params,
    })
    return data
  }

  static async getCommonDrillDowns(
    { metrics, month, quarter, year, startDate, endDate, viewType },
    { customCancelEvent } = {}
  ) {
    const params = merge({}, this.datepicker, {
      month,
      quarter,
      year,
      startDate,
      endDate,
      view: this.normalizeDatepickerView(viewType),
    })
    const body = {
      metrics,
    }
    const { data } = await this.request({ customCancelEvent }).post(
      `/${this.entity}/get-common-drilldowns`,
      body,
      {
        params,
      }
    )
    return data
  }

  static async getEnterprise({ metric, dealerships, period }, { customCancelEvent } = {}) {
    const params = {
      metric,
      dealerships: dealerships.join(','),
      period,
      ...this.datepicker,
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/enterprise`, {
      params,
    })

    return data
  }

  static async getFilterValues({ metric, filter }) {
    const { dealerGroupId, customGroupId } = this.datepicker
    const params = {
      metric,
      filter,
      dealerGroupId: !customGroupId ? dealerGroupId : 0,
      customGroupId,
    }
    const { data } = await this.request().get(`/${this.entity}/filter-values`, {
      params,
    })
    return data
  }

  static async getList({ excludeForPieChart = false } = {}) {
    const { dealerGroupId, customGroupId } = this.datepicker
    const options = this.normalizeOptions({
      excludeForPieChart: Number(excludeForPieChart),
    })

    const params = {
      dealerGroupId: !customGroupId ? dealerGroupId : 0,
      ...options,
    }
    const { data } = await this.request().get(`/${this.entity}/list`, {
      params,
    })
    return data
  }

  static async getMy({ type }) {
    const params = {
      type,
    }
    const { data } = await this.request().get(`/${this.entity}/my`, { params })
    return data
  }

  static async getTrend(
    { metric, dealerShipId = this.datepicker.dealerShipId },
    { customCancelEvent } = {}
  ) {
    const { dealerGroupId, customGroupId } = this.datepicker
    const params = {
      metric,
      dealerShipId, // optional
      dealerGroupId,
      customGroupId,
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/trend`, {
      params,
    })
    return data
  }

  static async getDailyTrend(
    { metric, period, month, quarter, year, startDate, endDate, viewType, dealerShipId },
    { customCancelEvent } = {}
  ) {
    const params = {
      metric,
      period,
      ...merge({}, this.datepicker, {
        month,
        quarter,
        year,
        startDate,
        endDate,
        view: this.normalizeDatepickerView(viewType),
        dealerShipId,
      }),
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/daily-trend`, {
      params,
    })

    return data
  }

  static async getWeeklyTrend(
    { metric, period, month, quarter, year, startDate, endDate, viewType, dealerShipId },
    { customCancelEvent } = {}
  ) {
    const params = {
      metric,
      period,
      ...merge({}, this.datepicker, {
        month,
        quarter,
        year,
        startDate,
        endDate,
        view: this.normalizeDatepickerView(viewType),
        dealerShipId,
      }),
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/weekly-trend`, {
      params,
    })

    return data
  }

  static async getMonthlyTrend(
    { metric, period, month, quarter, year, startDate, endDate, viewType, dealerShipId },
    { customCancelEvent } = {}
  ) {
    const params = {
      metric,
      period,
      ...merge({}, this.datepicker, {
        month,
        quarter,
        year,
        startDate,
        endDate,
        view: this.normalizeDatepickerView(viewType),
        dealerShipId,
      }),
    }

    const { data } = await this.request({ customCancelEvent }).get(
      `/${this.entity}/monthly-trend`,
      {
        params,
      }
    )

    return data
  }

  static async getRealtime() {
    const params = {
      ...this.datepicker,
    }
    const { data } = await this.request().get(`/${this.entity}/realtime`, {
      params,
    })
    return data
  }

  static async rename({ metric, name }) {
    const { dealerGroupId } = this.datepicker

    const body = { name }
    const params = {
      dealerGroupId,
      metric,
    }

    const { data } = await this.request().post(`/${this.entity}/rename`, body, {
      params,
    })

    return data
  }

  static async getDrillDown(
    { metric, drillDown, dealerShipId = this.datepicker.dealerShipId },
    { customCancelEvent } = {}
  ) {
    const params = {
      ...this.datepicker,
      metric,
      drillDown, // optional
      dealerShipId, // optional
    }
    const { data } = await this.request({ customCancelEvent }).get(`/${this.entity}/drill-down`, {
      params,
    })
    return data
  }

  static async getDrillDownValues({
    metric,
    filter,
    drillDown,
    period = 'current',
    dealerShipId = this.datepicker.dealerShipId,
  }) {
    const body = {
      filter,
    }

    const params = {
      ...this.datepicker,
      metric,
      drillDown, // optional
      dealerShipId, // optional
      period, // optional
    }

    const { data } = await this.request().post(`/${this.entity}/drill-down-values`, body, {
      params,
    })

    return data
  }

  static async getDrillDownSettings({ metric, metric2, drillDown }, { customCancelEvent } = {}) {
    const params = {
      ...this.datepicker,
      metric,
      metric2, // optional
      drillDown, // optional
    }
    const { data } = await this.request({ customCancelEvent }).get(
      `/${this.entity}/drill-down-settings`,
      {
        params,
      }
    )
    return data
  }

  static async restoreDefaults({ type }) {
    const params = { type }
    const { data } = await this.request({
      shouldCancelOnRoute: false,
      shouldCancelOnGet: false,
    }).post(`/${this.entity}/defaults`, {}, { params })
    return data
  }

  static async add({ metric }) {
    const body = {
      metric,
    }
    const { data } = await this.request({
      shouldCancelOnRoute: false,
      shouldCancelOnGet: false,
    }).post(`/${this.entity}/add`, body)
    return data
  }

  static async remove({ metric }) {
    const body = {
      metric,
    }
    const { data } = await this.request({
      shouldCancelOnRoute: false,
      shouldCancelOnGet: false,
    }).post(`/${this.entity}/remove`, body)
    return data
  }

  static async move({ position, metric, grouped = false }) {
    const body = {
      position,
    }
    const params = {
      metric,
      grouped: Number(grouped),
    }
    const { data } = await this.request({
      shouldCancelOnRoute: false,
      shouldCancelOnGet: false,
    }).post(`/${this.entity}/move`, body, {
      params,
    })
    return data
  }
}
